/*
 * ═══════════════════════════════════════════════════════════════════════════
 * THE SCOPE TRUTH DOC — what we clean, per service type. ONE source.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Before this file the "What's Included" checklist existed exactly once, hard-
 * coded in services.residential.tsx, and every other clean-type page either had
 * its own copy or none at all. There was no way to change the scope in one
 * place, which is what Vikelas asked for on 2026-09-10:
 *
 *   "Make sure there's only one scope truth doc, and make sure that any time we
 *    make a change to the truth doc, it gets reflected in each respective clean
 *    types scope list."
 *
 * ── How it works ───────────────────────────────────────────────────────────
 * BASE is the checklist every visit runs, whatever the service. Each service
 * then declares only what it ADDS on top (`extra`) and anything it explicitly
 * does NOT do (`excludes`). `scopeFor(service)` composes the final list.
 *
 * So: edit BASE and every service changes. Edit one service's `extra` and only
 * that service changes. There is no third place to look.
 *
 * ── Rules ──────────────────────────────────────────────────────────────────
 * 1. Never hardcode a checklist in a route again. Import `scopeFor()`.
 * 2. Add-on PRICES live in the pricing engine (src/shared/pricing.ts), not
 *    here — this file names the add-ons, `ADDON_PRICES` holds the money, and
 *    the two are checked against each other by scripts/verify-scope.ts.
 * 3. Keep items short and physical. "Wipe cabinet fronts", not "attend to
 *    kitchen surfaces". A client should be able to check whether we did it.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type ServiceScopeKey =
  | 'recurring'
  | 'oneTime'
  | 'deep'
  | 'moveOut';

export type ScopeGroup = {
  /** Room or area heading, e.g. "Kitchen". */
  title: string;
  items: string[];
};

/** The checklist every single visit runs, regardless of service type. */
export const BASE: ScopeGroup[] = [
  {
    title: 'All rooms',
    items: [
      'Dust all reachable surfaces, shelves and ledges',
      'Dust ceiling fans and light fixtures',
      'Vacuum carpets and rugs',
      'Mop hard floors',
      'Wipe mirrors and interior glass',
      'Empty trash and replace liners',
      'Make beds (on request)',
      'Straighten and reset each room before we leave',
    ],
  },
  {
    title: 'Kitchen',
    items: [
      'Clean and sanitize countertops',
      'Scrub sink and polish faucet',
      'Clean stovetop and control panel',
      'Wipe appliance exteriors — stove, microwave, fridge, dishwasher',
      'Wipe cabinet fronts and handles',
      'Clean backsplash',
      'Wipe down the table and chairs',
    ],
  },
  {
    title: 'Bathrooms',
    items: [
      'Scrub and disinfect toilet, inside and out',
      'Scrub tub and shower, including door tracks',
      'Clean and polish all fixtures',
      'Wipe vanity, sink and mirror',
      'Sanitize handles, switches and high-touch surfaces',
      'Mop floor',
      'Restock and fold towels',
    ],
  },
];

type ServiceScope = {
  label: string;
  /** One line describing who this is for. */
  summary: string;
  /** Groups added on top of BASE. */
  extra: ScopeGroup[];
  /** Stated plainly on the page so nobody is surprised on the day. */
  excludes: string[];
};

export const SERVICE_SCOPE: Record<ServiceScopeKey, ServiceScope> = {
  recurring: {
    label: 'Recurring clean',
    summary:
      'Weekly, biweekly or every four weeks. The same crew keeps the home at a steady standard.',
    extra: [
      {
        title: 'Because we come back',
        items: [
          'We rotate one detail area each visit — baseboards, doors, vents, under furniture',
          'We track what your home needs and adjust the routine to it',
          'Interior windows within reach on a rotating schedule',
        ],
      },
    ],
    excludes: [
      'Walls, ceilings and exterior windows',
      'Inside the oven or fridge (available as an add-on)',
      'Anything that needs a ladder above standard reach',
    ],
  },
  oneTime: {
    label: 'One-time clean',
    summary:
      'A single thorough visit for a home that is already in reasonable shape.',
    extra: [
      {
        title: 'Added detail',
        items: [
          'Baseboards wiped throughout',
          'Door frames, switch plates and handles',
          'Interior windows within reach',
        ],
      },
    ],
    excludes: [
      'Walls and ceilings',
      'Inside the oven or fridge (available as an add-on)',
      'Heavy build-up that needs a deep clean',
    ],
  },
  deep: {
    label: 'Deep clean',
    summary:
      'A full reset for a home that has been sitting, or a spring clean you have been putting off.',
    extra: [
      {
        title: 'Everything in a standard clean, plus',
        items: [
          'Inside and around vents and returns',
          'Interior windows, sills and tracks',
          'Behind and beneath movable furniture',
          'Grout scrubbed in kitchen and baths',
          'Trim, baseboards and door frames throughout',
          'Cabinet fronts degreased',
          'Light fixtures dusted and wiped',
        ],
      },
    ],
    excludes: [
      'Walls and ceilings',
      'Inside the oven or fridge (available as an add-on)',
      'Exterior windows',
    ],
  },
  moveOut: {
    label: 'Move-in / move-out',
    summary:
      'An empty house, cleaned end to end so it is ready to hand over or move into.',
    extra: [
      {
        title: 'Empty-home detail',
        items: [
          'Inside every cabinet, drawer and closet',
          'Inside the oven and the fridge, included at no extra charge',
          'All interior windows, sills and tracks',
          'Baseboards, trim and door frames throughout',
          'Vents, returns and light fixtures',
          'Appliances pulled forward where safe to do so',
        ],
      },
    ],
    excludes: [
      'Walls — at that point it is the painter’s job',
      'Ceilings and exterior windows',
      'Garage, attic and crawl spaces',
    ],
  },
};

/**
 * Priced add-ons. Names live here; the DOLLARS live in the pricing engine so
 * there is still exactly one money source. scripts/verify-scope.ts fails if
 * these two lists ever disagree.
 */
export const ADDONS = [
  { key: 'oven',    label: 'Inside the oven' },
  { key: 'fridge',  label: 'Inside the fridge' },
  { key: 'windows', label: 'Interior windows, whole home' },
  { key: 'laundry', label: 'Laundry washed and folded' },
] as const;

/** The full, composed checklist for a service. This is what pages render. */
export function scopeFor(service: ServiceScopeKey): ScopeGroup[] {
  return [...BASE, ...SERVICE_SCOPE[service].extra];
}

/** Total number of checklist items for a service — useful in copy ("62 points"). */
export function scopeItemCount(service: ServiceScopeKey): number {
  return scopeFor(service).reduce((n, g) => n + g.items.length, 0);
}
